import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import AduProfile from 'volta/models/adu-profile';
import {
  ADU_FREQUENCIES_KEYS,
  ADU_HORIZON_RANGES
} from 'volta/models/constants/adu';
import { TAduFrequency, TAduType } from 'volta/models/types/planning-settings';
import AduTsData, {
  IChartOptions,
  IDemandForecastTs
} from 'volta/services/adu-ts-data';
import LabelService from 'volta/services/label-service';
import {
  aduHorizonOffsetRatio,
  convertAduFrequencies,
  convertDailyAdu
} from 'volta/utils/adu-utils';
import { parseNumber } from 'volta/utils/math-utils';
import { getOrElse, isThenable } from 'volta/utils/object-utils';
import { btnGroupOptions } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { classify } from '@ember/string';
import { isNone } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  skuId: string;
  unitMeasure: string;
  aduProfileOptions: AduProfile[];
  aduProfileId?: string;
  aduType?: TAduType;
  aduTypes: Record<TAduType, string>;
  aduOverride?: number;
  aduMin?: number;
  aduHorizon?: number;
  aduHorizonOffset?: number;
  discontinuedDemand: boolean;
  showDiscontinued: boolean;
  aduChartOptions?: IChartOptions;
  showChart: boolean;
  onSubmit?: (data: IAduEditorSubmit) => void;
}

export interface IAduEditorSubmit {
  aduType?: TAduType;
  aduHorizon?: number;
  aduHorizonOffset?: number;
  aduMin?: number | null;
  aduOverride?: number | null;
  aduProfile?: AduProfile;
  aduProfileId?: string | null;
  discontinuedDemand?: boolean;
}

export default class AduFormEditor extends Component<IArgs> {
  // Services
  // ~~~~

  @service intl!: IntlService;
  @service aduTsData!: AduTsData;
  @service labelService!: LabelService;

  // Properties
  // ~~~~

  oldAduHorizon?: number;
  oldAduHorizonOffset?: number;

  @tracked aduType?: TAduType;
  @tracked aduHorizon?: number;
  @tracked aduHorizonOffset?: number;
  @tracked aduMin?: number | null;
  @tracked aduOverride?: number;
  @tracked aduProfile?: AduProfile;
  @tracked aduProfileId?: string;
  @tracked discontinuedDemand?: boolean;

  @tracked isChartLoading = false;
  @tracked showChartError = false;
  @tracked showDiscontinuedHint = false;
  @tracked chartData?: IDemandForecastTs;
  @tracked chartFrequency: TAduFrequency = 'DAILY';

  chartTimePeriod = '12M';
  aduFrequencies = ADU_FREQUENCIES_KEYS;

  // Computed Properties
  // ~~~~

  get showChart() {
    return getOrElse(this.args.showChart, true);
  }

  get selectedAduProfile() {
    const { aduProfileId } = this;
    const { aduProfileOptions = [] } = this.args;
    return aduProfileId ? aduProfileOptions.find((p) => p.id === aduProfileId) : undefined;
  }

  get aduHorizonRange() {
    return ADU_HORIZON_RANGES.DAILY;
  }

  get aduHorizonOffsetRange() {
    const { aduHorizon } = this;
    return aduHorizon
      ? {
          min: 0,
          max: Math.round(aduHorizon)
        }
      : undefined;
  }

  get selectedAduTypeLabel() {
    const { aduType } = this;
    return aduType ? `aduTypes.${aduType}` : 'none';
  }

  get aduTypesOptions() {
    return btnGroupOptions(this.args.aduTypes);
  }

  get isAduOverrideVisible() {
    const aduType = this.aduType ?? this.args.aduType;
    return (aduType ?? '').toLowerCase().indexOf('fixed') > -1;
  }

  /**
   * ADU Min converted to the frequency of the chart
   */
  get aduMinForChart() {
    const { aduType, aduMin } = this;
    return (
      aduType === 'ROLLING' && aduMin && convertDailyAdu(aduMin, this.chartFrequency || 'DAILY')
    );
  }

  /**
   * ADU Override converted to the frequency of the chart
   */
  get aduOverrideForChart() {
    const { aduType, aduOverride } = this;
    return (
      aduType === 'FIXED' &&
      aduOverride &&
      convertDailyAdu(aduOverride, this.chartFrequency || 'DAILY')
    );
  }

  get convertedAduMin() {
    const { unitMeasure } = this.args;
    const { aduMinForChart, chartFrequency, intl } = this;
    return aduMinForChart && chartFrequency !== 'DAILY'
      ? intl.t(`planningSettings.adu.equivalentTo${classify(chartFrequency.toLocaleLowerCase())}`, {
          value: intl.formatNumber(aduMinForChart),
          unit: this.labelService.unitMeasure(unitMeasure ?? 'UNIT')
        })
      : undefined;
  }

  get convertedAduFixed() {
    const { unitMeasure } = this.args;
    const { aduOverrideForChart, chartFrequency, intl } = this;
    return aduOverrideForChart && chartFrequency !== 'DAILY'
      ? intl.t(`planningSettings.adu.equivalentTo${classify(chartFrequency.toLocaleLowerCase())}`, {
          value: intl.formatNumber(aduOverrideForChart),
          unit: this.labelService.unitMeasure(unitMeasure ?? 'UNIT')
        })
      : undefined;
  }

  get convertedAduHorizon() {
    const aduHorizon = this.aduHorizon
      ? this.intl.formatNumber(
          convertAduFrequencies(this.aduHorizon, 'DAILY', this.chartFrequency) as number
        )
      : 0;
    return this.intl.t(`aduHorizonValue${classify(this.chartFrequency.toLocaleLowerCase())}`, {
      aduHorizon
    });
  }

  get convertedAduHorizonOffset() {
    const aduHorizon = this.aduHorizonOffset
      ? this.intl.formatNumber(
          convertAduFrequencies(this.aduHorizonOffset, 'DAILY', this.chartFrequency) as number
        )
      : 0;
    return this.intl.t(`aduHorizonValue${classify(this.chartFrequency.toLocaleLowerCase())}`, {
      aduHorizon
    });
  }

  get charts() {
    const fixed = this.aduType === 'FIXED';
    return {
      demandQty: { visible: true },
      forecastQty: { visible: true },
      adu: { visible: !fixed },
      aduMin: { visible: !fixed },
      aduOverride: { visible: fixed }
    };
  }

  // Functions
  // ~~~~

  @action
  handleUpdateProfile(_element: HTMLElement, [aduProfileOptions]: AduProfile[][]) {
    if (aduProfileOptions.length) {
      scheduleOnce('actions', this, () => {
        this.safeInitValues(this.selectedAduProfile);
      });
    }
  }

  @action
  handleDidInsertChart() {
    this.refreshChartTask.perform();
  }

  @action
  resetProfile() {
    const { aduProfileId } = this.args;

    if (isNone(aduProfileId)) {
      this.aduProfileId = undefined;
      this.initValues();
    }
  }

  // Actions
  // ~~~~

  @action
  onDiscontinuedChange() {
    this.discontinuedDemand = !this.discontinuedDemand;
    this.aduProfileId = undefined;
    return this.onSubmit();
  }

  @action
  onAduTypeChange(value: TAduType) {
    this.aduType = value;

    if (value !== 'ROLLING') {
      this.discontinuedDemand = false;
    }

    this.aduProfileId = undefined;

    return this.onSubmit();
  }

  @action
  onAduHorizonChange(value: number) {
    const aduHorizon = Math.round(value);
    this.onAduHorizonSlide(aduHorizon);
    if (this.showChart) {
      this.refreshChartTask.perform();
    }
    return this.onSubmit();
  }

  @action
  onAduHorizonSlide(value: number) {
    const newHorizon = Math.round(value);
    const oldHorizon = getOrElse(this.aduHorizon, this.oldAduHorizon) || 0;
    const oldOffset = getOrElse(
      getOrElse(this.aduHorizonOffset, this.oldAduHorizonOffset),
      oldHorizon
    );
    const ratio = aduHorizonOffsetRatio(oldHorizon, oldOffset);
    const newOffset = newHorizon * (ratio > 1 ? 1 : ratio);
    this.aduHorizon = newHorizon;
    this.aduHorizonOffset = Math.round(newOffset);
    this.aduProfileId = undefined;
  }

  @action
  onAduHorizonOffsetSlide(value: number) {
    this.aduHorizonOffset = Math.round(value);
    this.aduProfileId = undefined;
  }

  @action
  onAduHorizonOffsetChange(value: number) {
    this.onAduHorizonOffsetSlide(Math.round(value));
    if (this.showChart) {
      this.refreshChartTask.perform();
    }
    return this.onSubmit();
  }

  @action
  onAduOverrideChange(value: number) {
    this.aduProfileId = undefined;
    this.aduOverride = parseNumber(value);

    return this.onSubmit();
  }

  @action
  onAduMinChange(value?: number) {
    this.aduProfileId = undefined;
    if (isNone(value)) {
      this.aduMin = null;
    } else {
      this.aduMin = parseNumber(value);
    }
    return this.onSubmit();
  }

  @action
  changeChartFrequency(value: TAduFrequency) {
    this.aduProfileId = undefined;
    this.chartFrequency = value;
    if (this.showChart) {
      this.refreshChartTask.perform();
    }
    return this.onSubmit();
  }

  @action
  onAduProfileChange(profile: AduProfile) {
    this.safeInitValues(profile);
    if (this.showChart) {
      this.refreshChartTask.perform();
    }
    return this.onSubmit();
  }

  @action
  onSubmit() {
    return this.args.onSubmit?.(this.getDataToSubmit());
  }

  @action
  initValues(profile?: AduProfile) {
    const {
      aduHorizonOffset,
      aduProfileId,
      aduMin,
      aduOverride,
      aduHorizon,
      aduType,
      discontinuedDemand
    } = this.args;

    const _aduHorizonOffset = profile?.aduHorizonOffset ?? aduHorizonOffset;
    this.aduProfile = profile;
    this.aduProfileId = (profile?.id as string) ?? (aduProfileId as string);
    this.aduMin = profile ? profile.aduMin : aduMin;
    this.aduOverride = profile ? profile.aduOverride : aduOverride;

    this.oldAduHorizon = (profile?.aduHorizon as number) ?? aduHorizon ?? 1.0;
    this.aduHorizon = this.oldAduHorizon;
    this.oldAduHorizonOffset = (_aduHorizonOffset as number) ?? this.aduHorizon;
    this.aduHorizonOffset = this.oldAduHorizonOffset;
    this.aduType = (profile?.aduType as TAduType) ?? aduType ?? 'ROLLING';
    this.discontinuedDemand =
      (profile?.discontinuedDemand as boolean) ?? discontinuedDemand ?? false;
  }

  refreshChartTask = restartableTask(async () => {
    try {
      const { aduChartOptions = {}, skuId } = this.args;
      this.showChartError = false;
      const attrs = {
        ...aduChartOptions,
        skuId,
        timePeriod: this.chartTimePeriod,
        aduHorizon: this.aduHorizon,
        aduFrequency: this.chartFrequency,
        aduHorizonOffset: this.aduHorizonOffset
      } as IChartOptions;
      const chartData: IDemandForecastTs = await this.aduTsData.load(attrs);
      this.chartData = chartData;
      const volat = chartData.demandVolatility;
      if (volat && volat >= 1.5) {
        this.showDiscontinuedHint = true;
      }

      return chartData;
    } catch (error) {
      this.showChartError = true;
      return;
    }
  });

  safeInitValues(profile?: AduProfile | Promise<AduProfile>) {
    if (!profile) {
      return;
    }

    if (isThenable(profile as Promise<AduProfile>)) {
      (profile as Promise<AduProfile>).then((bp) => {
        this.initValues(bp);
      });
    } else {
      this.initValues(profile as AduProfile);
    }
  }

  getDataToSubmit(): IAduEditorSubmit {
    return {
      aduType: this.aduType,
      aduHorizon: this.aduHorizon,
      aduHorizonOffset: this.aduHorizonOffset,
      aduMin: this.aduMin,
      aduOverride: this.aduOverride,
      aduProfile: this.aduProfile,
      aduProfileId: this.aduProfileId,
      discontinuedDemand: this.discontinuedDemand
    };
  }
}
